/**
 * Export the whole session as one self-contained HTML page that looks like the
 * chat did.
 *
 * The Markdown and PDF exports are documents — the answer as prose to paste
 * somewhere else. This one is the conversation itself: the question in its
 * tinted bubble on the right, the written answer on the left in the chat's own
 * typography, the day pills between them, the theme the reader was using when
 * they clicked. Opened from disk it needs nothing — no network, no app, no
 * login, no font request — so every style is inlined and the theme tokens are
 * copied out of the live document rather than linked. Inter is named first in
 * the font stack and used where installed; the system UI face stands in
 * otherwise.
 *
 * What goes in and what stays out:
 *  - the question and the written answer, always;
 *  - the result rows, when the turn produced any — an HTML file renders a
 *    table well, which is the one thing the Markdown and PDF exports cannot,
 *    and the reader kept the table on screen beside the answer;
 *  - NOT the SQL or the API requests behind the answer, for the same reason
 *    the other exports leave them out: they name tables and filters that have
 *    no business in a forwarded document;
 *  - NOT the activity timeline or charts: the timeline is process, not
 *    content, and a chart's SVG is bound to the chart library's runtime.
 *
 * The Markdown rendering is injected (`renderMarkdown`) so this module stays
 * pure and testable; the caller passes `renderMarkdownToHtml` from
 * ./markdownHtml, which runs the chat's own react-markdown.
 */

import { answerText, exportedLine, isResultSet, isUserMessage, replaceCitationMarkers } from './answerExport';
import { looksLikeHTML } from './html';
import { sanitizeHTML } from './sanitize';
import { resultSet } from './resultSet';
import { splitMentions } from './mentions';

/** Rows a table carries before the export says "and N more". A reader who
 *  wants every row has the CSV/Excel downloads; this keeps the page a page. */
export const MAX_TABLE_ROWS = 100;

/** The semantic tokens a [data-theme] block defines — see theme/theme.css. */
export const THEME_TOKEN_NAMES = [
  '--bg-app', '--bg-surface', '--bg-raised', '--bg-sunken', '--bg-overlay',
  '--fg-primary', '--fg-secondary', '--fg-muted', '--fg-inverse',
  '--brand', '--brand-soft', '--brand-fg',
  '--success', '--success-soft', '--warning', '--warning-soft', '--danger', '--danger-soft',
  '--line', '--line-strong',
  '--grad-a', '--grad-b',
  '--shadow-soft', '--shadow-lift', '--shadow-float',
];

// Midnight, the default theme — the values :root carries in theme.css. Used
// when a token cannot be read (no document, or a token missing from the theme).
const DEFAULT_TOKENS = {
  '--bg-app': '#0a0d16',
  '--bg-surface': 'rgba(20, 25, 42, 0.66)',
  '--bg-raised': 'rgba(30, 37, 60, 0.72)',
  '--bg-sunken': '#070910',
  '--bg-overlay': 'rgba(13, 17, 30, 0.72)',
  '--fg-primary': '#eef1fb',
  '--fg-secondary': '#9aa6c8',
  '--fg-muted': '#5d6786',
  '--fg-inverse': '#0a0d16',
  '--brand': '#7c6bff',
  '--brand-soft': 'rgba(124, 107, 255, 0.16)',
  '--brand-fg': '#ffffff',
  '--success': '#4be3b0',
  '--success-soft': 'rgba(75, 227, 176, 0.15)',
  '--warning': '#ffb35c',
  '--warning-soft': 'rgba(255, 179, 92, 0.15)',
  '--danger': '#ff5d7a',
  '--danger-soft': 'rgba(255, 93, 122, 0.15)',
  '--line': 'rgba(124, 138, 196, 0.18)',
  '--line-strong': 'rgba(124, 138, 196, 0.34)',
  '--grad-a': '#7c6bff',
  '--grad-b': '#38d3ff',
  '--shadow-soft': '0 1px 2px rgba(0, 0, 0, 0.5)',
  '--shadow-lift': '0 8px 28px rgba(3, 5, 16, 0.55)',
  '--shadow-float': '0 18px 60px rgba(3, 5, 16, 0.7)',
};

const DARK_THEME_IDS = new Set(['dark', 'midnight', 'ocean', 'ember']);

/**
 * Snapshot the theme the reader is looking at: the [data-theme] id and every
 * token's computed value. Read from the live document rather than from a
 * table of themes so a white-label deployment that overrides a token exports
 * what it actually shows.
 */
export const readThemeTokens = (root = typeof document !== 'undefined' ? document.documentElement : null) => {
  const id = (root && root.getAttribute && root.getAttribute('data-theme')) || 'midnight';
  const tokens = { ...DEFAULT_TOKENS };
  if (root && typeof getComputedStyle === 'function') {
    const style = getComputedStyle(root);
    THEME_TOKEN_NAMES.forEach((name) => {
      const value = style.getPropertyValue(name).trim();
      if (value) tokens[name] = value;
    });
  }
  return { id, tokens, isDark: DARK_THEME_IDS.has(id) };
};

const escapeHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

/** Seconds or milliseconds since the epoch — the session API sends either. */
const toDate = (timestamp) => {
  if (timestamp == null || timestamp === '') return null;
  const ts = Number(timestamp);
  if (Number.isNaN(ts)) return null;
  const d = new Date(ts > 10000000000 ? ts : ts * 1000);
  return Number.isNaN(d.getTime()) ? null : d;
};

const sameDay = (a, b) => !!a && !!b
  && a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

const dayLabel = (d) => d.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const timeLabel = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });

const initialOf = (name) => (name && String(name).trim() ? String(name).trim().charAt(0).toUpperCase() : 'U');

/**
 * Written text as HTML. A description that arrives as HTML is sanitised and
 * kept; anything else is Markdown and goes through the injected renderer.
 * Citation markers go either way — the export has no reference panel.
 */
const proseHtml = (text, renderMarkdown) => {
  const clean = replaceCitationMarkers(answerText(text)).trim();
  if (!clean) return '';
  const html = looksLikeHTML(clean) ? sanitizeHTML(clean) : renderMarkdown(clean);
  return dropRemoteImages(html);
};

/**
 * The file promises to make no request when opened, and an answer can carry
 * `<img src="https://…">` — in an HTML description, or as Markdown `![]()` —
 * that the sanitiser rightly keeps. Only an embedded (data:) image stays; a
 * remote one becomes its alt text, in a marked span, so the reader sees that
 * something was there.
 */
const IMG_TAG_RE = /<img\b[^>]*>/gi;
const dropRemoteImages = (html) => html.replace(IMG_TAG_RE, (tag) => {
  const src = /\bsrc\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(tag);
  const value = src ? (src[2] ?? src[3] ?? src[4] ?? '') : '';
  if (/^data:image\//i.test(value.trim())) return tag;
  const alt = /\balt\s*=\s*("([^"]*)"|'([^']*)')/i.exec(tag);
  const label = alt ? (alt[2] ?? alt[3] ?? '') : '';
  return `<span class="img-omitted">${label || 'image not included'}</span>`;
});

const cellText = (value) => {
  if (value == null) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/** One result output as a table, capped, with its count. */
const tableHtml = (output, showLabel) => {
  const rows = output.rows.slice(0, MAX_TABLE_ROWS);
  const total = typeof output.totalRows === 'number' ? output.totalRows : output.rows.length;
  const columns = output.columns.length ? output.columns : Object.keys(rows[0] || {});
  const caption = `${total.toLocaleString()} ${total === 1 ? 'row' : 'rows'}`
    + (rows.length < total ? ` · showing the first ${rows.length.toLocaleString()}` : '');

  if (!rows.length) {
    return `<div class="result">
  ${showLabel ? `<div class="result-label">${escapeHtml(output.label)}</div>` : ''}
  <div class="result-note">${escapeHtml(caption)}${total > 0 ? ' — open the chat in the app to browse them.' : ' — no records to display.'}</div>
</div>`;
  }

  const head = columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('');
  const body = rows.map((r) => `<tr>${columns.map((c) => `<td>${escapeHtml(cellText(r[c]))}</td>`).join('')}</tr>`).join('\n');
  return `<div class="result">
  ${showLabel ? `<div class="result-label">${escapeHtml(output.label)}</div>` : ''}
  <div class="result-scroll"><table><thead><tr>${head}</tr></thead><tbody>
${body}
</tbody></table></div>
  <div class="result-note">${escapeHtml(caption)}</div>
</div>`;
};

const rowsHtml = (message) => {
  const set = resultSet(message);
  if (!set || !set.outputs?.length) return '';
  if (set.needsFetch) {
    // A reopened session: the turn has rows, but the session payload did not
    // replay them and this export asks the server for nothing. Say what is
    // known — the count when the turn carried one — and where the rows are.
    const total = typeof message.total_rows === 'number' ? message.total_rows : null;
    const count = total === null ? 'The result rows were not included in this export' : `${total.toLocaleString()} ${total === 1 ? 'row' : 'rows'}`;
    return `<div class="result"><div class="result-note">${escapeHtml(count)} — open the chat in the app to browse them.</div></div>`;
  }
  return set.outputs.map((o) => tableHtml(o, set.isMultiSource)).join('\n');
};

/** The assistant's turn: the written answer, then the rows if the turn had any. */
const answerHtml = (m, renderMarkdown) => {
  const written = proseHtml(m.description, renderMarkdown);
  const bodyIsProse = !!answerText(m.message).trim() && !isResultSet(m.message);
  const body = bodyIsProse ? proseHtml(m.message, renderMarkdown) : '';
  const rows = rowsHtml(m);

  const parts = [];
  if (written) parts.push(written);
  if (body && body !== written) parts.push(body);
  if (rows) parts.push(rows);
  if (!parts.length) parts.push('<p class="empty">This answer had no text.</p>');
  return parts.join('\n');
};

/**
 * The question, with each @mention wearing the chip it wore in the composer and
 * in the bubble on screen. This export is the conversation as it looked, so a
 * mention that was a chip in both places it appeared in the app should not
 * arrive here as plain words. No negative margin, unlike the app's rule: the
 * chip is only pinched in there to keep the highlight layer aligned with the
 * textarea underneath it, and there is no textarea in a saved page.
 */
const questionHtml = (m) => {
  const text = answerText(m.message).trim();
  const runs = splitMentions(text)
    .map((part) => (part.isMention ? `<span class="mention">${escapeHtml(part.text)}</span>` : escapeHtml(part.text)))
    .join('');
  return `<div class="q-text">${runs}</div>`;
};

/**
 * Build the document.
 *
 * @param {object} opts
 * @param {string}   opts.title          session summary
 * @param {Array}    opts.messages       the screen's own message list (liveChat)
 * @param {object}   [opts.theme]        from readThemeTokens()
 * @param {object}   [opts.brand]        { name, tagline, logoDataUrl } — the
 *                                       logo as a data URL so the file stays
 *                                       self-contained; see resolveBrand()
 * @param {object}   [opts.user]         { name } — the person exporting: their
 *                                       initial on the question bubbles, their
 *                                       name in the "Exported by" line
 * @param {Function} opts.renderMarkdown markdown string → HTML string
 * @param {Date}     [opts.exportedAt]
 */
export const buildSessionHtml = ({
  title,
  messages = [],
  theme = readThemeTokens(),
  brand = {},
  user = {},
  renderMarkdown = escapeHtml,
  exportedAt = new Date(),
} = {}) => {
  const docTitle = (title || '').trim() || 'Discover chat';
  const brandName = brand.name || 'Discover';
  const logo = brand.logoDataUrl && /^data:image\/(png|jpe?g);base64,[A-Za-z0-9+/=]+$/.test(brand.logoDataUrl) ? brand.logoDataUrl : null;
  const brandTagline = brand.tagline || '';
  const initial = initialOf(user.name);

  const body = [];
  let lastDay = null;
  let questions = 0;

  messages.forEach((m, index) => {
    // The default greeting the screen hides at the top of every chat.
    if (index === 0 && !isUserMessage(m.author_id) && !m.isError) return;
    const when = toDate(m.created_at);
    if (when && !sameDay(when, lastDay)) {
      body.push(`<div class="day"><span>${escapeHtml(dayLabel(when))}</span></div>`);
      lastDay = when;
    }
    const stamp = when ? `<div class="stamp">${escapeHtml(timeLabel(when))}</div>` : '';

    if (isUserMessage(m.author_id)) {
      if (!answerText(m.message).trim()) return;
      questions += 1;
      body.push(`<section class="turn user">
  <div class="q-row">
    <div class="q-bubble">${questionHtml(m)}</div>
    <div class="avatar" aria-hidden="true">${escapeHtml(initial)}</div>
  </div>
  ${stamp}
</section>`);
      return;
    }

    body.push(`<section class="turn bot${m.isError ? ' error' : ''}">
  <article class="markdown-content">
${answerHtml(m, renderMarkdown)}
  </article>
  ${stamp}
</section>`);
  });

  if (!body.length) body.push('<p class="empty">This chat had no messages.</p>');

  const tokenCss = THEME_TOKEN_NAMES.map((n) => `  ${n}: ${theme.tokens?.[n] || DEFAULT_TOKENS[n]};`).join('\n');
  const meta = `${exportedLine(user.name, exportedAt)} · ${questions} ${questions === 1 ? 'question' : 'questions'}`;

  return `<!doctype html>
<html lang="en" data-theme="${escapeHtml(theme.id || 'midnight')}">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(docTitle)} · ${escapeHtml(brandName)}</title>
<meta name="generator" content="${escapeHtml(brandName)} chat export">
<style>
:root {
${tokenCss}
  --chat-col: 760px;
  color-scheme: ${theme.isDark ? 'dark' : 'light'};
}
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body {
  background: var(--bg-app);
  color: var(--fg-primary);
  font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 15px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}
.wrap { width: 100%; max-width: var(--chat-col); margin: 0 auto; padding: 0 20px; }

/* Header — the app's top bar: wordmark left, the chat's title as the page's. */
.band { background: var(--bg-surface); border-bottom: 1px solid var(--line); padding: 20px 0 24px; }
.wordmark { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.wordmark .logo { height: 32px; width: auto; max-width: 200px; object-fit: contain; }
.wordmark .name { font-size: 18px; font-weight: 700; letter-spacing: -0.02em; }
.wordmark .tagline { font-size: 13px; font-weight: 500; letter-spacing: 0.02em; color: var(--fg-secondary); }
.band h1 { margin: 0; font-size: 22px; font-weight: 700; letter-spacing: -0.02em; line-height: 1.3; }
.band .meta { margin: 8px 0 0; font-size: 12px; color: var(--fg-muted); }

main { padding: 24px 0 64px; }

/* Day pills, as between the messages on screen. */
.day { display: flex; justify-content: center; margin: 16px 0; }
.day span { padding: 6px 12px; border-radius: 8px; font-size: 12px; font-weight: 500; background: var(--bg-raised); color: var(--fg-muted); }

.turn { margin: 0 0 20px; }
.stamp { font-size: 11px; color: var(--fg-muted); margin-top: 6px; }
.turn.user .stamp { text-align: right; padding-right: 48px; }

/* The question: a tinted bubble on the right with the corner pointing back
   at the sender, the reader's initial beside it. */
.q-row { display: flex; justify-content: flex-end; align-items: flex-start; gap: 12px; }
.q-bubble { max-width: 85%; padding: 12px 16px; border-radius: 16px 16px 6px 16px; background: var(--brand-soft); border: 1px solid var(--line); }
.q-text { font-size: 14px; line-height: 1.6; white-space: pre-wrap; overflow-wrap: anywhere; }
.q-text .mention { border-radius: 7px; padding: 1px 2px; background: var(--brand-soft); background: color-mix(in srgb, var(--brand) 30%, transparent); box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--brand) 60%, transparent); -webkit-box-decoration-break: clone; box-decoration-break: clone; }
.avatar { flex: none; width: 36px; height: 36px; border-radius: 999px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 600; background: var(--brand); color: var(--brand-fg); }

/* The answer sits directly on the page, no card, no avatar — one speaker per side. */
.turn.bot article { max-width: 100%; }
.turn.error article { border-left: 3px solid var(--danger); padding-left: 12px; }
.empty { color: var(--fg-muted); font-style: italic; }
.img-omitted { display: inline-block; padding: 2px 8px; border: 1px dashed var(--line-strong); border-radius: 6px; font-size: 12px; color: var(--fg-muted); }

/* .markdown-content — ported from the app's stylesheet so prose renders as on screen. */
.markdown-content { font-size: 15px; line-height: 1.6; color: var(--fg-primary); overflow-wrap: break-word; word-break: break-word; }
.markdown-content > :first-child { margin-top: 0; }
.markdown-content h1, .markdown-content h2, .markdown-content h3, .markdown-content h4, .markdown-content h5, .markdown-content h6 { color: var(--fg-primary); margin-top: 1rem; margin-bottom: 0.75rem; font-weight: 600; line-height: 1.3; }
.markdown-content h1 { font-size: 2rem; }
.markdown-content h2 { font-size: 1.5rem; }
.markdown-content h3 { font-size: 1.125rem; font-weight: 700; letter-spacing: -0.01em; }
.markdown-content h4 { font-size: 1rem; font-weight: 600; margin-top: 1rem; margin-bottom: 0.5rem; }
.markdown-content p { margin: 0 0 1rem; line-height: 1.7; }
.markdown-content ul, .markdown-content ol { margin: 0.5rem 0 1.25rem; padding-left: 1.75rem; }
.markdown-content li { margin-bottom: 0.625rem; line-height: 1.6; }
.markdown-content code { background: var(--bg-sunken); padding: 4px 8px; border-radius: 6px; font-size: 0.8125rem; color: var(--danger); font-family: Monaco, Menlo, "Courier New", monospace; font-weight: 500; border: 1px solid var(--line); }
.markdown-content pre { background: var(--bg-sunken); padding: 16px; border-radius: 8px; overflow-x: auto; margin: 1rem 0; border: 1px solid var(--line); }
.markdown-content pre code { background: transparent; color: var(--fg-primary); padding: 0; border: 0; font-weight: 400; }
.markdown-content a { color: var(--brand); text-decoration: none; font-weight: 500; }
.markdown-content a:hover { text-decoration: underline; }
.markdown-content blockquote { border-left: 4px solid var(--brand); padding-left: 1rem; margin: 1rem 0; color: var(--fg-muted); font-style: italic; }
.markdown-content hr { border: 0; border-top: 1px solid var(--line); margin: 1.25rem 0; }
.markdown-content img { max-width: 100%; }
.markdown-content table { width: 100%; margin: 1rem 0; border-collapse: collapse; }
.markdown-content table th, .markdown-content table td { padding: 0.75rem; border: 1px solid var(--line); text-align: left; }
.markdown-content table th { background: var(--bg-raised); font-weight: 600; color: var(--fg-primary); }
.markdown-content table td { color: var(--fg-secondary); }

/* Result rows: the data panel, laid under the answer. */
.result { margin: 1rem 0 0; border: 1px solid var(--line); border-radius: 12px; background: var(--bg-surface); overflow: hidden; }
.result-label { padding: 10px 14px 0; font-size: 12px; font-weight: 600; color: var(--fg-secondary); }
.result-scroll { overflow-x: auto; }
.result table { width: 100%; margin: 0; border-collapse: collapse; font-size: 13px; }
.result th, .result td { padding: 8px 12px; border-bottom: 1px solid var(--line); text-align: left; white-space: nowrap; }
.result th { background: var(--bg-raised); font-weight: 600; color: var(--fg-primary); position: sticky; top: 0; }
.result td { color: var(--fg-secondary); font-variant-numeric: tabular-nums; }
.result tbody tr:last-child td { border-bottom: 0; }
.result-note { padding: 8px 14px; font-size: 12px; color: var(--fg-muted); border-top: 1px solid var(--line); }

footer { border-top: 1px solid var(--line); padding: 16px 0 32px; font-size: 12px; color: var(--fg-muted); }

@media print {
  body { background: #fff; color: #16181d; }
  .band { border-bottom-color: #e7eaef; }
  .q-bubble { break-inside: avoid; }
  .result-scroll { overflow: visible; }
}
</style>
</head>
<body>
<header class="band">
  <div class="wrap">
    <div class="wordmark">${logo ? `<img class="logo" src="${logo}" alt="">` : ''}<span class="name">${escapeHtml(brandName)}</span>${brandTagline ? `<span class="tagline">${escapeHtml(brandTagline)}</span>` : ''}</div>
    <h1>${escapeHtml(docTitle)}</h1>
    <p class="meta">${escapeHtml(meta)}</p>
  </div>
</header>
<main class="wrap">
${body.join('\n')}
</main>
<footer><div class="wrap">${escapeHtml(docTitle)} · ${escapeHtml(meta)}</div></footer>
</body>
</html>
`;
};
